<script lang="ts" setup>
import { watch } from 'vue'

import { MultiSelect, MultiSelectObjectOption } from '@collector/shared-ui'
import { Venue } from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { useEvent, useSport } from '@desktop/views/Relation/composables'

import { injectSuccessBadge } from '../../successBadge'
import { useSearchTermOptions } from '../composables'

type OptionValue = { id: number; name: string }

// inject
const { showSuccessBadge } = injectSuccessBadge()

// composables
const { sport } = useSport()
const { event } = useEvent()

const {
  selectedValue,
  selectedValueOptions,
  searchTerm,
  options,
  defaultOptions,
} = useSearchTermOptions(fetchSelectOptions)

// methods
async function fetchSelectOptions(
  searchTermValue?: string,
): Promise<MultiSelectObjectOption[]> {
  const venues = await sportsApiClient.Venues.get({
    sport_id: sport.value.id,
    short_name: searchTermValue,
  })

  return (venues.api.data.venues || []).map(getOption)
}

function getOption(venue: Venue): MultiSelectObjectOption {
  return {
    name: venue.short_name,
    value: getOptionValue(venue),
  }
}

function getOptionValue(venue: Venue): {
  id: number
  name: string
} {
  return {
    id: venue.id,
    name: venue.short_name,
  }
}

async function updateEventVenue(
  newValue: OptionValue | undefined,
): Promise<void> {
  selectedValue.value = newValue
  const venueId = newValue?.id ?? null

  await sportsApiClient.Events.patch(event.value.id, {
    event: { venue_id: venueId },
  })

  // if value was cleared, reset options to default
  if (!newValue) {
    options.value = defaultOptions
  }

  // after updating event in API, venue_id is not available in received queueRabbitClient
  // - Rabbit message doesn't contain venue_id
  // - we update venue_id manually in the event object
  event.value.venue_id = venueId

  showSuccessBadge()
}

async function setVenueFromEvent(): Promise<void> {
  if (event.value.venue_id === null) {
    selectedValue.value = undefined
    return
  }

  // fetch current venue_id from API, create single option in multiselect, then select option
  const response = await sportsApiClient.Venues.getById(event.value.venue_id)
  const venue = response.api.data.venue

  if (venue) {
    const venueOptions = [getOption(venue)]

    options.value = venueOptions
    selectedValueOptions.value = venueOptions

    if (event.value.venue_id !== (selectedValue.value as OptionValue)?.id) {
      selectedValue.value = getOptionValue(venue)
    }
  }
}

// init
watch(() => event.value.venue_id, setVenueFromEvent, { immediate: true })
</script>

<template>
  <MultiSelect
    v-model:searchTerm="searchTerm"
    :modelValue="selectedValue"
    withFilter
    withClear
    :options
    @update:modelValue="updateEventVenue($event as OptionValue)"
  >
    <span class="w-12">Venue</span>
    <template
      v-if="selectedValue"
      #displayedValue
    >
      {{ (selectedValue as OptionValue).name }}
    </template>
  </MultiSelect>
</template>
